import Swiper from 'swiper';
import {
  Navigation,
  Pagination,
  A11y,
  Autoplay,
  Keyboard,
  HashNavigation,
  EffectFade,
} from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

import { FOCUSABLE_SELECTORS } from './focusableSelectors';

export function initPrCarouselSwiper(container, options = {}) {
  if (!container) return null;

  // Root = outer <section class="pr-carousel-advanced">
  const root =
    options.rootEl ||
    container.closest('.pr-carousel-advanced') ||
    container;

  // Kill any previous instance on this node
  if (container.swiper?.destroy) {
    try { container.swiper.destroy(true, true); } catch {}
  }

  // a11y label if not already applied by PHP
  if (options.ariaLabel && !root.hasAttribute('aria-label')) {
    root.setAttribute('role', 'region');
    root.setAttribute('aria-label', String(options.ariaLabel));
  }

  // Respect prefers-reduced-motion for autoplay
  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const autoplayConfig = reduceMotion ? false : (options.autoplay || false);

  const hasNavigation = !!options.navigation;
  const hasPagination = !!options.pagination;

  // Optional extra CSS vars from editor.
  if (options.cssVars && typeof options.cssVars === 'object') {
    for (const [k, v] of Object.entries(options.cssVars)) {
      if (v != null && v !== '') root.style.setProperty(k, String(v));
    }
  }

  const modules = [A11y, Keyboard];
  if (hasNavigation) modules.push(Navigation);
  if (hasPagination) modules.push(Pagination);
  if (autoplayConfig) modules.push(Autoplay);
  if (options.hashNavigation) modules.push(HashNavigation);
  if ((options.effect || 'slide') === 'fade') modules.push(EffectFade);

  // Controls are on the root.
  const nextEl = root.querySelector('.pr-carousel-advanced__button-next');
  const prevEl = root.querySelector('.pr-carousel-advanced__button-prev');
  const pagEl  = root.querySelector('.pr-carousel-advanced__pagination');

  const pagination =
    hasPagination && pagEl
      ? {
          el: pagEl,
          type: options.paginationType || 'bullets',
          clickable:
            (options.paginationType || 'bullets') === 'bullets'
              ? !!options.paginationClickable
              : undefined,
          dynamicBullets:
            (options.paginationType || 'bullets') === 'bullets'
              ? !!options.paginationDynamicBullets
              : undefined,
        }
      : undefined;

  // Support both {base, breakpoints} or direct core values
  const baseSPV = options.base?.slidesPerView ?? options.slidesPerView ?? 1;
  const baseGap = options.base?.spaceBetween  ?? options.spaceBetween  ?? 0;

  const swiper = new Swiper(container, {
    modules,
    init: false,

    direction: options.direction || 'horizontal',
    effect: options.effect || 'slide',
    ...(options.effect === 'fade' ? { fadeEffect: { crossFade: true } } : {}),

    speed: options.speed ?? 300,
    loop: !!options.loop,
    rewind: !!options.rewind,
    autoHeight: !!options.autoHeight,
    centeredSlides: !!options.centeredSlides,
    grabCursor: !!options.grabCursor,

    slidesPerView: baseSPV,
    spaceBetween:  baseGap,
    breakpoints:   options.breakpoints || undefined,

    watchOverflow: true,
    watchSlidesProgress: true,
    preloadImages: true,

    navigation: hasNavigation && nextEl && prevEl ? { nextEl, prevEl } : undefined,
    pagination,
    autoplay: autoplayConfig || false,

    // Free mode can be boolean or object ({enabled, sticky})
    freeMode: options.freeMode || false,

    keyboard: { enabled: true, onlyInViewport: false },
    a11y: { enabled: true },

    hashNavigation: options.hashNavigation ? { watchState: true } : undefined,
  });

  const focusableSelectors = options.focusableSelectors || FOCUSABLE_SELECTORS;
  const shouldAutoFocusActive = !autoplayConfig;

  const getSlides = () => container.querySelectorAll('.swiper-slide');

  const updateFocusableElements = () => {
    const slides = getSlides();
    slides.forEach((slide, index) => {
      const focusables = slide.querySelectorAll(focusableSelectors.join(','));
      const isActive = index === swiper.activeIndex;

      focusables.forEach((el) => {
        if (isActive) {
          el.removeAttribute('tabindex');
          el.removeAttribute('aria-hidden');
        } else {
          el.setAttribute('tabindex', '-1');
          el.setAttribute('aria-hidden', 'true');
        }
      });

      if (isActive && shouldAutoFocusActive && focusables.length > 0) {
        focusables[0].focus({ preventScroll: true });
      }
    });
  };

  const handleKeyNavigation = (event) => {
    if (event.key === 'ArrowRight') swiper.slideNext();
    else if (event.key === 'ArrowLeft') swiper.slidePrev();
  };

  if (!root.hasAttribute('tabindex')) {
    root.setAttribute('tabindex', '0');
  }

  swiper.on('init', updateFocusableElements);
  swiper.on('slideChange', updateFocusableElements);

  swiper.init();
  updateFocusableElements();

  // Observe DOM changes to slides
  const wrapper = container.querySelector('.swiper-wrapper');
  let observer;
  if (wrapper && typeof MutationObserver !== 'undefined') {
    observer = new MutationObserver(() => {
      swiper.update();
      updateFocusableElements();
    });
    observer.observe(wrapper, { childList: true });
  }

  root.addEventListener('keydown', handleKeyNavigation);

  swiper.on('destroy', () => {
    root.removeEventListener('keydown', handleKeyNavigation);
    if (observer) observer.disconnect();
  });

  container.swiper = swiper;
  return swiper;
}